import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useImperativeHandle,
  useRef,
} from 'react';

import {omit} from 'es-toolkit';
import styled from 'styled-components';

import type {WithDomStyleProp} from '../../utils/utility-types';
import type {Props, Ref} from './types';

import {useController} from './controller';
import {Container, web} from './styles';

const StyledTextInput = styled.input.attrs<
  Omit<Props, 'autoCorrect'> & {autoCorrect: 'no' | 'yes' | undefined}
>(p => ({
  // type comes from {...webProps}
  type: p.secureTextEntry ? 'password' : p.type,
}))`
  ${web};
`;

const TextInput = forwardRef<Ref, WithDomStyleProp<Props>>((props, ref) => {
  const {
    blurHandler,
    changeHandler,
    focusHandler,
    keyPressHandler,
    renderClearButton,
    renderError,
    renderSearchIcon,
    value,
  } = useController(props);

  const {
    appearance,
    autoCorrect,
    containerStyle,
    disabled,
    error,
    isClearable,
    onSubmit,
    testID,
    webProps,
    ...rest
  } = omit(props, ['onClear']);

  const input = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    ref,
    () =>
      ({
        blur: () => input.current?.blur(),
        clear: () => {
          if (input.current) {
            input.current.value = '';
          }
        },
        focus: () => input.current?.focus(),
        ref: {
          web: input,
        },
        scrollTo: config => {
          const rect = input.current?.getBoundingClientRect();

          requestAnimationFrame(() => {
            window.scrollTo({
              behavior: 'smooth',
              left: rect?.x || 0,
              top: (rect?.y || 0) + (config?.offset || 0),
            });
          });
        },
        select: () => input.current?.select(),
      }) as Ref,
  );

  const onKeyDownCb = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSubmit?.();
    }

    keyPressHandler(e);
  };

  const normalizedAutoCorrect =
    autoCorrect === undefined ? undefined : autoCorrect ? 'yes' : 'no';

  return (
    <Container style={containerStyle}>
      <StyledTextInput
        {...rest}
        {...webProps}
        appearance={appearance}
        autoCorrect={normalizedAutoCorrect}
        data-test-id={testID}
        disabled={disabled}
        error={error}
        isClearable={isClearable}
        onBlur={blurHandler}
        onChange={changeHandler}
        onFocus={focusHandler}
        onKeyDown={onKeyDownCb}
        // it doesn't fire on "backspace" or "enter" press
        onKeyPress={undefined}
        onSubmit={onSubmit}
        readOnly={disabled}
        ref={input}
        value={value}
      />
      {renderSearchIcon()}
      {renderError()}
      {renderClearButton()}
    </Container>
  );
});

export {TextInput};
